import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  QuerySnapshot,
  Timestamp,
} from "firebase/firestore";
import {
  CategoryResult,
  CompetitionResult,
  CompetitionWithId,
  FinalResults,
  JudgeResult,
  Result,
  RoundResult,
  RoundType,
} from "../interfaces/ICompetitions";
import { db } from "../App";

type ResultsCompetitionDocument = {
  competitionId?: string;
  name?: string;
  fullName?: string;
  location?: string;
  date?: string;
  displaySettings?: CompetitionResult["displaySettings"];
  updatedAt?: Timestamp;
};

type ResultsCategoryDocument = {
  competitionId?: string;
  categoryId?: string;
  index?: number;
  name?: string;
  fullName?: string;
  day?: number;
  publicationScopes?: CategoryResult["publicationScopes"];
  finalResults?: {
    judges?: JudgeResult[];
    rounds?: FinalResults["rounds"];
    rows?: {
      place?: number;
      presentationId?: string;
      name?: string;
      title?: string;
      rounds?: string[];
      roundResults?: {
        roundId: string;
        value: string;
      }[];
    }[];
    finalPlaceRankingMode?: "standard" | "dense";
    publishedAt?: Timestamp;
  };
  updatedAt?: Timestamp;
};

type ResultsRoundDocument = {
  roundId?: string;
  stageCode?: string;
  type?: number;
  name?: string;
  judges?: JudgeResult[];
  results?: {
    presentationId?: string;
    presentationName?: string;
    promotion?: boolean;
    place?: number | null;
    votes?: string[];
  }[];
};

const sortCategories = (categories: CategoryResult[]) =>
  [...categories].sort(
    (categoryA, categoryB) =>
      categoryA.index - categoryB.index ||
      (categoryA.day ?? Number.MAX_SAFE_INTEGER) - (categoryB.day ?? Number.MAX_SAFE_INTEGER) ||
      categoryA.name.localeCompare(categoryB.name, "pl", { numeric: true, sensitivity: "base" }),
  );

const mapRoundResult = (roundData: ResultsRoundDocument, roundId: string): RoundResult => ({
  roundId: roundData.roundId || roundId,
  stageCode: roundData.stageCode,
  type: Number(roundData.type ?? RoundType.ELIMINATIONS) as RoundType,
  name: roundData.name || "",
  judges: roundData.judges || [],
  results: (roundData.results || []).map(
    (resultRow): Result => ({
      place: resultRow.place ?? undefined,
      promotion: resultRow.promotion,
      presentation: {
        id: resultRow.presentationId || "",
        name: resultRow.presentationName || "",
      },
    }),
  ),
});

const mapFinalResults = (
  finalResultsData: ResultsCategoryDocument["finalResults"],
  publicationScopes?: CategoryResult["publicationScopes"],
): FinalResults | undefined => {
  if (!finalResultsData) {
    return undefined;
  }

  const rounds = finalResultsData.rounds || [];
  const rows = (finalResultsData.rows || []).map((row) => {
    const roundResults = row.roundResults || [];

    return {
      place: row.place ?? 0,
      presentationId: row.presentationId || "",
      name: row.name || "",
      title: row.title,
      rounds:
        row.rounds ||
        rounds.map((round) => roundResults.find((roundResult) => roundResult.roundId === round.roundId)?.value || ""),
      roundResults,
    };
  });

  return {
    judges: finalResultsData.judges || [],
    rounds,
    rows,
    finalPlaceRankingMode: finalResultsData.finalPlaceRankingMode,
    publicationScopes: {
      category: publicationScopes?.categoryFinal === true,
      competition: publicationScopes?.competitionFinal === true,
    },
    publishedAt: finalResultsData.publishedAt as Timestamp,
  };
};

const mapCategoryResult = ({
  competitionId,
  categoryId,
  categoryData,
  rounds,
}: {
  competitionId: string;
  categoryId: string;
  categoryData: ResultsCategoryDocument;
  rounds: RoundResult[];
}): CategoryResult => ({
  categoryId: categoryData.categoryId || categoryId,
  index: Number(categoryData.index || 0),
  name: categoryData.name || "",
  fullName: categoryData.fullName || "",
  day: typeof categoryData.day === "number" ? categoryData.day : undefined,
  publicationScopes: categoryData.publicationScopes,
  rounds,
  finalResults: mapFinalResults(categoryData.finalResults, categoryData.publicationScopes),
});

const mapCompetitionResult = ({
  competitionId,
  competitionData,
  categories,
}: {
  competitionId: string;
  competitionData: ResultsCompetitionDocument;
  categories: CategoryResult[];
}): CompetitionResult => ({
  id: competitionId,
  competitionId: competitionData.competitionId || competitionId,
  name: competitionData.name || "",
  fullName: competitionData.fullName || "",
  location: competitionData.location || "",
  date: competitionData.date || "",
  categories: sortCategories(categories),
  displaySettings: competitionData.displaySettings,
  updatedAt: competitionData.updatedAt as Timestamp,
});

const loadCompetitionCategories = async (
  competitionId: string,
  includeRounds: boolean,
): Promise<CategoryResult[]> => {
  const categoriesRef = collection(db, "results", competitionId, "categories");
  const categoriesSnapshot = await getDocs(categoriesRef);

  return Promise.all(
    categoriesSnapshot.docs.map(async (categorySnapshot) => {
      const categoryData = categorySnapshot.data() as ResultsCategoryDocument;
      let rounds: RoundResult[] = [];

      if (includeRounds) {
        const roundsSnapshot = await getDocs(collection(categorySnapshot.ref, "rounds"));
        rounds = roundsSnapshot.docs.map((roundSnapshot) =>
          mapRoundResult(roundSnapshot.data() as ResultsRoundDocument, roundSnapshot.id),
        );
      }

      return mapCategoryResult({
        competitionId,
        categoryId: categorySnapshot.id,
        categoryData,
        rounds,
      });
    }),
  );
};

export const findCompetitionsFromResults = async (): Promise<CompetitionWithId[]> => {
  try {
    const resultsRef = collection(db, "results");
    const snapshot = await getDocs(resultsRef);
    const competitions = await Promise.all(
      snapshot.docs.map(async (competitionSnapshot) => {
        const competitionData = competitionSnapshot.data() as ResultsCompetitionDocument;
        const categories = await loadCompetitionCategories(competitionSnapshot.id, false);

        return mapCompetitionResult({
          competitionId: competitionSnapshot.id,
          competitionData,
          categories,
        });
      }),
    );

    return competitions;
  } catch (error) {
    console.error("Firestore error:", error);
    throw error;
  }
};

export const findCompetitionResultById = async (
  competitionId: string,
): Promise<CompetitionResult | null> => {
  try {
    const resultRef = doc(db, "results", competitionId);
    const resultDoc = await getDoc(resultRef);
    if (!resultDoc.exists()) return null;

    const competitionData = resultDoc.data() as ResultsCompetitionDocument;
    const categories = await loadCompetitionCategories(competitionId, true);

    return mapCompetitionResult({
      competitionId: resultDoc.id,
      competitionData,
      categories,
    });
  } catch (error) {
    console.error("Firestore error:", error);
    throw error;
  }
};

export const subscribeCompetitionsFromResults = (
  onData: (competitions: CompetitionWithId[]) => void,
  onError: (error: Error) => void,
) => {
  const resultsRef = collection(db, "results");
  /** Przyrostowo: pełne unsubscribe wszystkich `categories` przy każdym snapshocie `results` powodowało
   * błędy wewnętrzne SDK (WatchChangeAggregator), np. gdy pojawiał się nowy dokument `results` (stub harmonogramu). */
  const categoryUnsubByCompetitionId = new Map<string, () => void>();
  let disposed = false;

  const emitData = async () => {
    try {
      const competitions = await findCompetitionsFromResults();
      if (!disposed) {
        onData(competitions);
      }
    } catch (error) {
      if (!disposed) {
        onError(error instanceof Error ? error : new Error("Wystąpił błąd podczas odczytu wyników."));
      }
    }
  };

  const syncCategoryListeners = (snapshot: QuerySnapshot) => {
    const ids = new Set(snapshot.docs.map((d) => d.id));

    const staleCompetitionIds: string[] = [];
    categoryUnsubByCompetitionId.forEach((unsub, competitionId) => {
      if (!ids.has(competitionId)) {
        unsub();
        staleCompetitionIds.push(competitionId);
      }
    });
    staleCompetitionIds.forEach((id) => {
      categoryUnsubByCompetitionId.delete(id);
    });

    snapshot.docs.forEach((competitionSnapshot) => {
      const id = competitionSnapshot.id;
      if (categoryUnsubByCompetitionId.has(id)) {
        return;
      }
      const unsub = onSnapshot(
        collection(competitionSnapshot.ref, "categories"),
        () => {
          void emitData();
        },
        (error) => {
          console.error("Firestore error:", error);
          if (!disposed) {
            onError(error);
          }
        },
      );
      categoryUnsubByCompetitionId.set(id, unsub);
    });
  };

  const unsubscribe = onSnapshot(
    resultsRef,
    (snapshot) => {
      syncCategoryListeners(snapshot);
      void emitData();
    },
    (error) => {
      console.error("Firestore error:", error);
      onError(error);
    },
  );

  void emitData();

  return () => {
    disposed = true;
    unsubscribe();
    categoryUnsubByCompetitionId.forEach((unsub) => {
      unsub();
    });
    categoryUnsubByCompetitionId.clear();
  };
};

export const subscribeCompetitionResultById = (
  competitionId: string,
  onData: (competition: CompetitionResult | null) => void,
  onError: (error: Error) => void,
) => {
  const resultRef = doc(db, "results", competitionId);
  const categoriesRef = collection(resultRef, "categories");
  let roundUnsubscribers: Array<() => void> = [];
  let disposed = false;

  const clearRoundSubscriptions = () => {
    roundUnsubscribers.forEach((unsubscribe) => unsubscribe());
    roundUnsubscribers = [];
  };

  const emitData = async () => {
    try {
      const competition = await findCompetitionResultById(competitionId);
      if (!disposed) {
        onData(competition);
      }
    } catch (error) {
      if (!disposed) {
        onError(error instanceof Error ? error : new Error("Wystąpił błąd podczas odczytu turnieju."));
      }
    }
  };

  const competitionUnsubscribe = onSnapshot(
    resultRef,
    () => {
      void emitData();
    },
    (error) => {
      console.error("Firestore error:", error);
      if (!disposed) {
        onError(error);
      }
    },
  );

  const categoriesUnsubscribe = onSnapshot(
    categoriesRef,
    (categoriesSnapshot) => {
      clearRoundSubscriptions();

      categoriesSnapshot.docs.forEach((categorySnapshot) => {
        roundUnsubscribers.push(
          onSnapshot(
            collection(categorySnapshot.ref, "rounds"),
            () => {
              void emitData();
            },
            (error) => {
              console.error("Firestore error:", error);
              if (!disposed) {
                onError(error);
              }
            },
          ),
        );
      });

      void emitData();
    },
    (error) => {
      console.error("Firestore error:", error);
      if (!disposed) {
        onError(error);
      }
    },
  );

  void emitData();

  return () => {
    disposed = true;
    competitionUnsubscribe();
    categoriesUnsubscribe();
    clearRoundSubscriptions();
  };
};
